import type { ServiceMapper, ServiceType } from '../../../domain/types'
import type { ImpressionViewAncillariesItem } from '@/scopes/checkout/domain/analytics/types'
import type {
  CheckoutClothingSelectedValue,
  CheckoutClothingService,
  CheckoutClothingServiceOption,
} from '@/scopes/checkout/domain/types/services/clothing'
import type { APIBookingDraft, APIBookingDraftRoom } from '@/scopes/checkout/infrastructure/api/types'
import type {
  APIBookingDraftClothingService,
  APIBookingDraftClothingServiceOption,
} from '@/scopes/checkout/infrastructure/api/types/services/clothing'
import { isNil, values } from 'ramda'
import { GENDERS_ORDER, NO_GENDER, SIZES_ORDER } from '@/scopes/checkout/components/services/ClothingService/constants'
import {
  createServiceParticipantsMapper,
  type ServiceParticipantsMapper,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceParticipantsMapper'
import {
  type GA4ImpressionItemsConfig,
  mapDefaultGA4ImpressionItems,
  mapDefaultSelectedItemToGA4,
  type SelectedItemInput,
} from '../model-mappers/ga4AnalyticsMapper'
import { createServiceOptionMapper, type ServiceOptionMapper } from '../model-mappers/serviceOptionMapper'

interface Dependencies {
  serviceName: string
  serviceTypeMapper: string
  serviceOptionMapper?: ServiceOptionMapper
  serviceParticipantsMapper?: ServiceParticipantsMapper
  serviceType: ServiceType
}

export const createClothingServiceMapper: (dependencies: Dependencies) => ServiceMapper<CheckoutClothingService> = (
  dependencies: Dependencies,
) => {
  const {
    serviceName,
    serviceTypeMapper,
    serviceOptionMapper = createServiceOptionMapper(),
    serviceParticipantsMapper = createServiceParticipantsMapper(),
    serviceType,
  } = dependencies

  function getServiceName() {
    return serviceName
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper
  }

  function map(bookingDraft: APIBookingDraft) {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName) as APIBookingDraftClothingService

    if (!service)
      return null

    const { aName, options } = service

    const participants
      = bookingDraft?.participants.reduce(
        (acc, participant) => ({
          ...acc,
          [participant.id.toString()]: {
            ...serviceParticipantsMapper.mapParticipantWithAvailableOptions(participant, options),
            selectedValue:
              buildUniqOptionId(
                options
                  .filter(({ isSelected }) => isSelected)
                  .find(({ assignedParticipants }) => assignedParticipants.includes(participant.id)),
              ) || undefined,
            availableOptions: options
              .filter(({ appliesToParticipants }) => appliesToParticipants.includes(participant.id))
              .map(option => buildUniqOptionId(option)),
          },
        }),
        {},
      ) ?? {}

    const anyParticipantHasSelectedValue = values(participants).some(({ selectedValue }) => selectedValue)
    return {
      id: aName,
      type: serviceType,
      isSelected: anyParticipantHasSelectedValue ?? !isNil(anyParticipantHasSelectedValue),
      rooms: bookingDraft?.rooms.map(mapRoom) ?? [],
      participants,
      isNoneOption: buildUniqOptionId(options.find(({ isNoneOption }) => isNoneOption)) ?? '',
      options: options.reduce(
        (acc2: Record<string, CheckoutClothingServiceOption>, option) => ({
          ...acc2,
          [buildUniqOptionId(option)]: {
            ...serviceOptionMapper.map(option, service, bookingDraft),
            id: buildUniqOptionId(option),
            gender: option.gender,
            size: option.size,
          },
        }),
        {},
      ),
      editingSelectedValues: options.reduce((acc3: Record<string, CheckoutClothingSelectedValue>, option) => {
        if (option.isSelected) {
          option.assignedParticipants.forEach((participantId) => {
            acc3[participantId] = {
              gender: option.gender,
              size: option.size,
              value: option.code,
              id: buildUniqOptionId(option),
            }
          })
        }
        else {
          bookingDraft.participants.forEach((participant) => {
            if (acc3[participant.id.toString()])
              return

            let genders = Array.from(
              new Set(
                Object.values(options)
                  .filter(({ gender }) => gender !== NO_GENDER)
                  .map(({ gender }) => gender),
              ),
            )
            genders = genders.sort((a, b) => GENDERS_ORDER.indexOf(a) - GENDERS_ORDER.indexOf(b))
            if (genders.length < 0)
              return

            let sizes = Array.from(
              new Set(
                Object.values(options)
                  .filter(({ gender }) => gender === genders[0])
                  .map(({ size }) => size),
              ),
            )
            sizes = sizes.sort((a, b) => SIZES_ORDER.indexOf(a) - SIZES_ORDER.indexOf(b))
            if (sizes.length < 0)
              return

            let defaultOption = options.find(({ gender, size }) => gender === genders[0] && size === sizes[0])
            if (!defaultOption) {
              defaultOption = options.find(({ isNoneOption }) => !isNoneOption)
            }
            acc3[participant.id.toString()] = {
              gender: defaultOption?.gender ?? '',
              size: defaultOption?.size ?? '',
              value: defaultOption?.code ?? '',
              id: buildUniqOptionId(defaultOption) ?? '',
            }
          })
        }
        return acc3
      }, {}),
      sizes: options.reduce(
        (acc4: Record<string, { sizes: string[] }>, option) => ({
          ...acc4,
          [option.gender]: {
            sizes: options
              .filter(({ gender, isNoneOption }) => gender === option.gender && !isNoneOption)
              .map(({ size }) => size),
          },
        }),
        {},
      ),
    }
  }

  function mapRoom({ id, name, participantIds }: APIBookingDraftRoom) {
    return {
      id: id.toString(),
      name,
      participantIds: participantIds.map(participantId => participantId.toString()),
    }
  }

  function mapSelection(service: CheckoutClothingService) {
    const { participants, options } = service
    return Object.values(participants ?? {}).map(({ id, selectedValue }) => {
      const selectedOption = options[selectedValue ?? '']
      return {
        participantId: id,
        value: selectedOption?.value ?? null,
      }
    })
  }

  function buildUniqOptionId(option: APIBookingDraftClothingServiceOption | undefined) {
    if (!option)
      return ''
    return option.code.replaceAll(' ', '_').toLowerCase()
  }

  function mapGA4ImpressionItems(bookingDraft: APIBookingDraft, config?: GA4ImpressionItemsConfig): ImpressionViewAncillariesItem[] {
    const service = bookingDraft.services.find(({ aName }) => aName === serviceName)
    if (!service)
      return []
    return mapDefaultGA4ImpressionItems(service, bookingDraft, serviceOptionMapper, config, serviceType)
  }

  function mapSelectedItemToGA4(service: CheckoutClothingService, step: number): ImpressionViewAncillariesItem[] {
    const seenItemIds = new Set<string>()
    const selectedItems = values(service.participants ?? {}).reduce((items: SelectedItemInput[], participant) => {
      if (!participant.selectedValue)
        return items

      const option = service.options[participant.selectedValue]
      if (!option)
        return items

      if (seenItemIds.has(option.value))
        return items

      seenItemIds.add(option.value)
      items.push({
        label: option.label || '',
        value: option.value,
        price: option.price,
        rawPrice: option.rawPrice,
        badge: option.badge,
      })

      return items
    }, [])

    return mapDefaultSelectedItemToGA4(selectedItems, {
      serviceType,
      serviceId: service.id,
      step,
    })
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapGA4ImpressionItems,
    mapSelectedItemToGA4,
  }
}
